import React from 'react';
import { useHistory } from 'react-router-dom';
import { css } from '@emotion/react';
import { BHTheme } from '../../types/bh-themes.types';
import * as CLASSES from '../../constants/class-names';
import Header from '../header/themed.header.component';
import Footer from '../../components/footer/footer.component';
import {
  setUKCountry,
  UK_COUNTRY_TO_VANITY,
} from '../../services/signup-service';

const COUNTRIES = [
  { key: 'england', label: 'England' },
  { key: 'scotland', label: 'Scotland' },
  { key: 'wales', label: 'Wales' },
  { key: 'northern_ireland', label: 'Northern Ireland' },
] as const;

type UKCountryKey = typeof COUNTRIES[number]['key'];

type Props = { redirectUrl: string };

const CountrySelector: React.FC<Props> = ({ redirectUrl }) => {
  const history = useHistory();
  const qs = redirectUrl
    ? `?redirect_url=${encodeURIComponent(redirectUrl)}`
    : '';

  const handleSelect = (country: UKCountryKey) => {
    setUKCountry(country);
    history.push(`/sleepio/${UK_COUNTRY_TO_VANITY[country]}${qs}`);
  };

  return (
    <div id={CLASSES.SL_FLOW} css={pageStyles}>
      <Header />
      <div role="main" css={mainStyles}>
        <h1 css={titleStyles}>Where do you live?</h1>
        <ul css={listStyles}>
          {COUNTRIES.map(({ key, label }) => (
            <li key={key} css={listItemStyles}>
              <button
                type="button"
                css={optionStyles}
                onClick={() => handleSelect(key)}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <Footer />
    </div>
  );
};

const pageStyles = (theme: BHTheme) =>
  css({
    color: theme.fonts.fontColors.PRIMARY,
    lineHeight: 1.2,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    fontSize: theme.fonts.fontSizes.MEDIUM,
    '> :last-child': { marginTop: 'auto' },
  });

const mainStyles = (theme: BHTheme) =>
  css({
    padding: `0 ${theme.padding.LARGE}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexGrow: 1,
  });

const titleStyles = (theme: BHTheme) =>
  css({
    marginTop: 34,
    fontSize: theme.fonts.fontSizes.TITLE,
    textAlign: 'center',
  });

const listStyles = () =>
  css({
    listStyle: 'none',
    margin: '32px 0 0',
    padding: 0,
    width: '100%',
    maxWidth: 400,
  });

const listItemStyles = () =>
  css({
    marginBottom: 12,
  });

const optionStyles = (theme: BHTheme) =>
  css({
    width: '100%',
    padding: '16px',
    border: `1px solid ${theme.inputs.radio.borderColor}`,
    borderRadius: theme.borders.defaultBorderRadius,
    background: 'transparent',
    color: theme.fonts.fontColors.PRIMARY,
    fontSize: theme.fonts.fontSizes.STANDARD,
    cursor: 'pointer',
    textAlign: 'left',
    '&:hover': {
      border: `1px solid ${theme.inputs.radio.selectedBorderColor}`,
      backgroundColor: theme.button.backgroundColor.hover,
      color: theme.button.color.default,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.inputs.radio.selectedBorderColor}`,
      outlineOffset: 2,
    },
  });

export default CountrySelector;
